import React from "react";
import Link from "next/link";
import Image from "next/image";
import moment from "moment";
import PropTypes from "prop-types";

const BlogCard = ({ post }) => {
    // Format the published date
    const formatDate = (dateString) => {
        return moment(dateString).format("MMM DD, YYYY");
    };

    // Get reading time or default to 1 min
    const readingTime = post.reading_time || 1;

    // Get excerpt or truncate content
    const getExcerpt = () => {
        if (post.custom_excerpt) {
            return post.custom_excerpt;
        }
        if (post.excerpt) {
            return post.excerpt;
        }
        // If no excerpt, create one from html content
        const text = post.html?.replace(/<[^>]*>/g, '') || '';
        return text.length > 160 ? text.substring(0, 160) + '...' : text;
    };    return (        <Link href={`/blog/${post.slug}`} className="block h-full">
            <div className="group bg-white rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer h-full flex flex-col">
                {/* Featured Image */}
                <div className="relative h-48 bg-gray-200 overflow-hidden flex-shrink-0">
                    {post.feature_image ? (
                        <Image
                            src={post.feature_image}
                            alt={post.feature_image_alt || post.title}
                            fill
                            className="object-cover group-hover:scale-105 transition-transform duration-300"
                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                            unoptimized
                        />
                    ) : (
                        <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                            <span className="text-gray-500 text-sm">No Image</span>
                        </div>
                    )}

                    {/* Featured badge */}
                    {post.featured && (
                        <div className="absolute top-4 left-4 bg-emerald-500 text-white px-2 py-1 text-xs font-semibold rounded">
                            Featured
                        </div>
                    )}
                </div>

                {/* Content */}
                <div className="p-6 flex flex-col flex-grow">                    {/* Tags */}
                    {post.tags && post.tags.filter(tag => !tag.name.startsWith('#')).length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                            {post.tags.filter(tag => !tag.name.startsWith('#')).slice(0, 2).map((tag) => (
                                <Link
                                    key={tag.id}
                                    href={`/blog/tag/${tag.slug}`}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <span className="inline-block bg-gray-100 text-gray-600 hover:bg-emerald-100 hover:text-emerald-700 text-xs px-2 py-1 rounded-full transition-colors cursor-pointer">
                                        {tag.name}
                                    </span>
                                </Link>
                            ))}
                        </div>
                    )}

                    {/* Title */}
                    <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-emerald-600 transition-colors duration-200">
                        {post.title}
                    </h3>

                    {/* Excerpt - Limited to 2 lines */}
                    <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-2 flex-grow">
                        {getExcerpt()}
                    </p>                    {/* Meta information */}
                    <div className="flex items-center justify-between text-xs text-gray-500 mt-auto">
                        <div className="flex items-center space-x-2">
                            {/* Author */}
                
                            <span>{formatDate(post.published_at)}</span>
                        </div>
                        <span>{readingTime} min read</span>
                    </div>
                </div>
            </div>
        </Link>    );
};

BlogCard.propTypes = {
    post: PropTypes.shape({
        slug: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        feature_image: PropTypes.string,
        feature_image_alt: PropTypes.string,
        custom_excerpt: PropTypes.string,
        excerpt: PropTypes.string,
        html: PropTypes.string,
        published_at: PropTypes.string.isRequired,
        reading_time: PropTypes.number,
        featured: PropTypes.bool,
        tags: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.string,
            name: PropTypes.string,
        })),
        primary_author: PropTypes.shape({
            slug: PropTypes.string,
            name: PropTypes.string,
        }),
    }).isRequired,
};

export default BlogCard;
